import { useEffect, useState } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { updatePrimaryAgentOrPond, getAssignableAgents } from "@shared/serviceHelpers";
import ManageAccessModal from "../modals/ManageAccessModal";
import NonPrimaryTeamMembers from "./NonPrimaryTeamMembers";
import CollapsibleElement from "../CollapsibleElement";
import AgentOrPondSelect from "../../../shared/AgentOrPondSelect";
import * as actions from "../../actions/personDetailActionCreators";
import Loading from "../../../Goals/components/Loader";
import { useCurrentUser } from "../../../reducers/layoutReducer/selectors";
import AppointmentRemoveWarning from "../modals/AppointmentRemoveWarning";

const getAffectedAgentNames = (appointments, preservedAgentUuids, teamMembers) => {
  const preservedAgents = new Set(preservedAgentUuids);
  const affectedAgents = new Map();

  (appointments || []).forEach((appointment) => {
    // Only incomplete appointments are removed on reassignment, so a completed one
    // costs its owner nothing and must not trigger the warning.
    if (appointment.state === "completed") return;

    const agentUuid = appointment.assigned_to_user_id;
    if (!agentUuid || preservedAgents.has(agentUuid) || affectedAgents.has(agentUuid)) return;

    const teamMemberName = teamMembers.find((member) => member.uuid === agentUuid)?.name;
    affectedAgents.set(agentUuid, appointment.user_name || teamMemberName || "someone");
  });

  return Array.from(affectedAgents.values());
};

const AssignedTo = () => {
  const dispatch = useDispatch();
  const currentUser = useCurrentUser();
  const person = useSelector((state) => state.personDetail.person);
  const manageAccessModalOpen = useSelector((state) => state.personDetail.manageAccessModalOpen);
  const [loading, setLoading] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [primaryAgent, setPrimaryAgent] = useState(undefined);
  const [allAssignableRoles, setAllAssignableRoles] = useState([]);
  const [appointmentRemoveWarning, setAppointmentRemoveWarning] = useState(false);
  const appointments = useSelector((state) => state.personDetail.appointments);
  const [pendingAgentChange, setPendingAgentChange] = useState(null);
  const [leadPond, setLeadPond] = useState(person.data?.attributes.lead_pond);
  const [affectedAgentNames, setAffectedAgentNames] = useState([]);
  const [adminUserUuids, setAdminUserUuids] = useState([]);

  const openManageAccessModal = () => dispatch(actions.openManageAccessModal());
  const closeManageAccessModal = () => dispatch(actions.closeManageAccessModal());

  const getPersonPermissions = (signal = null, isPrimary = false) =>
    axios
      .get(`/person_permissions?person_id=${person.data?.id}`, {
        signal,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })
      .then((response) => {
        setTeamMembers(response.data);
        setPrimaryAgent(response.data.find((agent) => agent.primary === true));
        if (isPrimary) {
          setLeadPond(null);
        }
      })

  useEffect(() => {
    if (!person.data?.id) return () => {};
    setLeadPond(person.data?.attributes.lead_pond);
    const abortController = new AbortController();

    setLoading(true);

    const permissionPromise = getPersonPermissions(abortController.signal);
    const rolesPromise = axios
      .get("/api/v4/team_roles", {
        signal: abortController.signal,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })
      .then((response) => {
        setAllAssignableRoles(response.data);
      })

    Promise.allSettled([permissionPromise, rolesPromise]).then(() => setLoading(false));

    return () => abortController.abort();
  }, [person.data?.id]);

  useEffect(() => {
    // /account_active_agents returns the full account roster with each agent's
    // user_type, so we can reliably identify admins — including one who owns an
    // appointment on this contact but holds only implicit (account-level) access
    // and therefore never appears in /person_permissions (teamMembers).
    getAssignableAgents(
      ({ data }) => {
        setAdminUserUuids(
          data.filter((agent) => agent.user_type === "Admin").map((agent) => agent.uuid),
        );
      },
      () => {},
    );
  }, []);

  const handleUpdateAccess = () => {
    // Because we need the team member id which isn't returned from the POST request in the modal
    getPersonPermissions(null, true);
  };

  const handleAgentOrPondChangeSuccess = () => {
    dispatch(actions.getAppointments(person.data?.attributes?.uuid));
    // Do NOT pass isPrimary here: AgentOrPondSelect's onSuccess callback already sets
    // leadPond correctly (the pond for a pond change, null for an agent change). Forcing
    // leadPond to null would make a pond assignment render the pond's display agent.
    getPersonPermissions(null);
  };

  const handleBeforeAgentChange = (newAgent, params) => {
    // Only the pond -> agent reassignment shows the warning. For agent -> agent we
    // keep the synchronous appointment reassignment behavior (no warning).
    if (!leadPond) return true;

    const preservedAgentUuids = [newAgent?.uuid, ...adminUserUuids].filter(Boolean);
    const affectedNames = getAffectedAgentNames(
      appointments,
      preservedAgentUuids,
      teamMembers,
    );

    if (affectedNames.length === 0) return true;

    setAffectedAgentNames(affectedNames);
    setPendingAgentChange({ params });
    setAppointmentRemoveWarning(true);
    return false;
  };

  const handleBeforePondChange = (pond, params) => {
    // Only the agent -> pond reassignment shows the warning. For pond -> pond we
    // keep the synchronous appointment reassignment behavior (no warning), mirroring
    // the source-direction guard in handleBeforeAgentChange.
    if (leadPond) return true;

    const pondMemberUuids = (pond.accessible_by_users || []).map((user) => user.uuid);
    const preservedAgentUuids = [
      ...adminUserUuids,
      pond.display_agent?.uuid,
      ...pondMemberUuids,
    ].filter(Boolean);
    const affectedNames = getAffectedAgentNames(
      appointments,
      preservedAgentUuids,
      teamMembers,
    );

    if (affectedNames.length === 0) return true;

    setAffectedAgentNames(affectedNames);
    setPendingAgentChange({ params, pond });
    setAppointmentRemoveWarning(true);
    return false;
  };

  const handleAppointmentWarningClose = () => {
    setPendingAgentChange(null);
    setAppointmentRemoveWarning(false);
    setAffectedAgentNames([]);
  };

  const handleAppointmentWarningSubmit = () => {
    if (!pendingAgentChange) return;
    // pond is set for an agent -> pond change, and null for a pond -> agent change.
    const { params, pond = null } = pendingAgentChange;
    updatePrimaryAgentOrPond({
      data: params,
      errorCb: () => {},
      successCb: (response) => {
        const newUuid = response.data.primary_agent_uuid;
        dispatch(actions.updatePrimaryAgent(newUuid));
        dispatch(actions.getAppointments(person.data?.attributes?.uuid));
        // Preserve the pond as primary so the card shows the pond name, not its
        // display agent (pond is null for a pond -> agent change, showing the agent).
        setLeadPond(pond);
        getPersonPermissions(null);
      },
    });
    setPendingAgentChange(null);
    setAppointmentRemoveWarning(false);
    setAffectedAgentNames([]);
  };

  // TODO: do can there be a person without a primary agent and if so what do we render
  const nonPrimaryTeamMembers = teamMembers.filter(
    // This is why we need the team member id, filter out whitelisted agents that are not team members
    (teamMember) => teamMember.id && teamMember.primary === false,
  );

  return (
    <>
      <CollapsibleElement
        id="pdp-assigned-to"
        title="Assigned To:"
        buttonText={
          currentUser.account.isAdmin || currentUser.uuid === person.data?.attributes.primary_agent_uuid
            ? "MANAGE"
            : undefined
        }
        buttonClickFunction={openManageAccessModal}
        roundedTop
      >
        {(!person.data || loading) && <Loading />}

        <div
          id="person-assigned-to-card"
          className="tw-flex tw-flex-col tw-gap-[24px]"
          data-cy="assigned-to-wrapper"
        >
          {primaryAgent && (
            <AgentOrPondSelect
              personId={person.data?.id}
              primaryAgent={primaryAgent}
              leadPond={leadPond}
              setLeadPond={setLeadPond}
              setPrimaryAgent={setPrimaryAgent}
              onBeforeAgentChange={handleBeforeAgentChange}
              onBeforePondChange={handleBeforePondChange}
              onChangeSuccess={handleAgentOrPondChangeSuccess}
            />
          )}

          {nonPrimaryTeamMembers && !leadPond && (
            <NonPrimaryTeamMembers
              nonPrimaryTeamMembers={nonPrimaryTeamMembers}
              allAssignableRoles={allAssignableRoles}
            />
          )}

          {manageAccessModalOpen && (
            <ManageAccessModal
              personId={person.data?.id}
              nonPrimaryTeamMembers={nonPrimaryTeamMembers}
              handleUpdateAccess={handleUpdateAccess}
              closeManageAccessModal={closeManageAccessModal}
              primaryAgent={primaryAgent}
              allAssignableRoles={allAssignableRoles}
            />
          )}
        </div>
      </CollapsibleElement>
      <AppointmentRemoveWarning
        show={appointmentRemoveWarning}
        onClose={handleAppointmentWarningClose}
        onSubmit={handleAppointmentWarningSubmit}
        previousAgents={affectedAgentNames}
      />
    </>
  );
};

export default AssignedTo;
